import { useState, useEffect, useRef } from "react";
import { MapPin, X, Search, Check, LocateFixed, Loader2, Home, Building2, Star, Plus, Trash2 } from "lucide-react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { toast } from "sonner";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pinIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click(e) {
      onLocationSelect([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

function RecenterMap({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, 16, { duration: 1 });
  }, [center, map]);
  return null;
}

const LABEL_ICONS = {
  Home: Home,
  Hospital: Building2,
};

export default function LocationPicker({ sessionId, onConfirm, onClose }) {
  const [position, setPosition] = useState([28.6139, 77.209]);
  const [address, setAddress] = useState("New Delhi, India");
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [mapCenter, setMapCenter] = useState([28.6139, 77.209]);
  const [savedLocations, setSavedLocations] = useState([]);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [saving, setSaving] = useState(false);

  // Load saved locations
  useEffect(() => {
    if (sessionId) {
      axios.get(`${API}/locations/${sessionId}`)
        .then((res) => setSavedLocations(res.data.locations || []))
        .catch(() => {});
    }
  }, [sessionId]);

  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        { headers: { "Accept-Language": "en" } }
      );
      const data = await res.json();
      if (data.display_name) {
        setAddress(data.display_name);
      } else {
        // Fallback to coordinates
        setAddress(`Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      }
    } catch (e) {
      console.error("Reverse geocode failed:", e);
      // Fallback to coordinates on error
      setAddress(`Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    }
  };

  const searchLocation = async (query) => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=in&limit=1`,
        { headers: { "Accept-Language": "en" } }
      );
      const data = await res.json();
      if (data.length > 0) {
        const loc = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
        setPosition(loc);
        setMapCenter(loc);
        setAddress(data[0].display_name);
      }
    } catch (e) {
      console.error("Search failed:", e);
    } finally {
      setSearching(false);
    }
  };

  const handleLocationSelect = (latlng) => {
    setPosition(latlng);
    // Set immediate fallback so button is enabled right away
    setAddress(`Location: ${latlng[0].toFixed(6)}, ${latlng[1].toFixed(6)}`);
    reverseGeocode(latlng[0], latlng[1]);
  };

  const getCurrentLocation = async () => {
    setLocating(true);
    // Try browser geolocation first
    if (navigator.geolocation) {
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 8000,
            maximumAge: 60000,
          });
        });
        const loc = [pos.coords.latitude, pos.coords.longitude];
        setPosition(loc);
        setMapCenter(loc);
        setAddress(`Location: ${loc[0].toFixed(6)}, ${loc[1].toFixed(6)}`);
        reverseGeocode(loc[0], loc[1]);
        setLocating(false);
        return;
      } catch (geoErr) {
        console.warn("Browser geolocation failed:", geoErr.message);
      }
    }
    // Fallback: IP-based geolocation
    try {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();
      if (data.latitude && data.longitude) {
        const loc = [data.latitude, data.longitude];
        setPosition(loc);
        setMapCenter(loc);
        setAddress(data.city ? `${data.city}, ${data.region || ""}` : `Location: ${loc[0].toFixed(6)}, ${loc[1].toFixed(6)}`);
        reverseGeocode(loc[0], loc[1]);
        toast.info("Using approximate location based on your network.");
      } else {
        toast.error("Could not detect location. Please search or tap the map.");
      }
    } catch {
      toast.error("Location access failed. Please search for your address or tap the map.");
    }
    setLocating(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { getCurrentLocation(); }, []); // async is fine here

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      searchLocation(searchQuery);
    }
  };

  const handleConfirm = () => {
    if (address) {
      onConfirm({ lat: position[0], lng: position[1], address });
    }
  };

  const selectSavedLocation = (loc) => {
    const coords = [loc.lat, loc.lng];
    setPosition(coords);
    setMapCenter(coords);
    setAddress(loc.address);
  };

  const saveCurrentLocation = async () => {
    if (!saveLabel.trim() || !address || !sessionId) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/locations/save`, {
        session_id: sessionId,
        label: saveLabel.trim(),
        address,
        lat: position[0],
        lng: position[1],
      });
      // Update local list
      setSavedLocations((prev) => {
        const filtered = prev.filter((l) => l.label !== saveLabel.trim());
        return [...filtered, res.data];
      });
      toast.success(`"${saveLabel.trim()}" saved`);
      setShowSaveForm(false);
      setSaveLabel("");
    } catch (e) {
      toast.error("Could not save location");
    } finally {
      setSaving(false);
    }
  };

  const deleteSavedLocation = async (locId, label) => {
    try {
      await axios.delete(`${API}/locations/${locId}`);
      setSavedLocations((prev) => prev.filter((l) => l.id !== locId));
      toast.success(`"${label}" removed`);
    } catch (e) {
      toast.error("Could not remove location");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="location-picker-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: "85vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ backgroundColor: "#2D2766" }}>
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-white" />
            <h2 className="text-white font-semibold text-lg">Share Location</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors" data-testid="close-location-btn">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Saved locations */}
        {savedLocations.length > 0 && (
          <div className="px-4 pt-3 pb-1 border-b bg-gray-50" data-testid="saved-locations-bar">
            <p className="text-xs text-gray-500 font-medium mb-2">Saved places</p>
            <div className="flex flex-wrap gap-2 pb-2">
              {savedLocations.map((loc) => {
                const IconComp = LABEL_ICONS[loc.label] || Star;
                return (
                  <div key={loc.id} className="group relative">
                    <button
                      onClick={() => selectSavedLocation(loc)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border-2 border-[#2D2766]/20 text-[#2D2766] text-sm font-medium hover:bg-[#2D2766] hover:text-white transition-all active:scale-95"
                      data-testid={`saved-loc-${loc.label.toLowerCase().replace(/\s+/g, '-')}`}
                      title={loc.address}
                    >
                      <IconComp className="w-4 h-4" />
                      {loc.label}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteSavedLocation(loc.id, loc.label); }}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full items-center justify-center hidden group-hover:flex shadow-sm"
                      data-testid={`delete-loc-${loc.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Search bar */}
        <div className="px-4 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search for a place..."
                className="w-full pl-9 pr-4 py-2.5 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D2766]"
                data-testid="location-search-input"
              />
            </div>
            <button
              onClick={() => searchLocation(searchQuery)}
              disabled={searching || !searchQuery.trim()}
              className="px-4 py-2.5 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50"
              style={{ backgroundColor: "#2D2766" }}
              data-testid="location-search-btn"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
            </button>
          </div>
          <button
            onClick={getCurrentLocation}
            disabled={locating}
            className="flex items-center gap-2 mt-2 text-sm text-[#2D2766] font-medium hover:underline"
            data-testid="use-current-location-btn"
          >
            <LocateFixed className={`w-4 h-4 ${locating ? "animate-spin" : ""}`} />
            {locating ? "Getting location..." : "Use my current location"}
          </button>
        </div>

        {/* Map */}
        <div style={{ height: "280px", width: "100%" }} data-testid="location-map">
          <MapContainer center={mapCenter} zoom={14} style={{ height: "100%", width: "100%" }} zoomControl={true}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={position} icon={pinIcon} />
            <MapClickHandler onLocationSelect={handleLocationSelect} />
            <RecenterMap center={mapCenter} />
          </MapContainer>
        </div>

        {/* Address display + save + confirm */}
        <div className="px-4 py-3 border-t bg-white">
          {address ? (
            <div className="mb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1 font-medium">Selected location:</p>
                  <p className="text-sm text-gray-800 leading-relaxed" data-testid="selected-address">{address}</p>
                </div>
              </div>

              {/* Save location toggle */}
              {!showSaveForm ? (
                <button
                  onClick={() => setShowSaveForm(true)}
                  className="flex items-center gap-1.5 mt-2 text-xs text-[#2D2766] font-medium hover:underline"
                  data-testid="save-location-toggle"
                >
                  <Star className="w-3.5 h-3.5" />
                  Save this place
                </button>
              ) : (
                <div className="flex items-center gap-2 mt-2" data-testid="save-location-form">
                  {/* Quick label buttons */}
                  <div className="flex gap-1.5">
                    {["Home", "Hospital"].map((preset) => (
                      <button
                        key={preset}
                        onClick={() => setSaveLabel(preset)}
                        className={`px-2.5 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          saveLabel === preset
                            ? "bg-[#2D2766] text-white border-[#2D2766]"
                            : "bg-white text-gray-600 border-gray-300 hover:border-[#2D2766]"
                        }`}
                        data-testid={`preset-${preset.toLowerCase()}`}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={saveLabel}
                    onChange={(e) => setSaveLabel(e.target.value)}
                    placeholder="Or type a name..."
                    className="flex-1 text-sm border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#2D2766]"
                    data-testid="save-label-input"
                  />
                  <button
                    onClick={saveCurrentLocation}
                    disabled={!saveLabel.trim() || saving}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg text-white transition-colors disabled:opacity-40"
                    style={{ backgroundColor: "#2D2766" }}
                    data-testid="save-location-btn"
                  >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                  </button>
                  <button
                    onClick={() => { setShowSaveForm(false); setSaveLabel(""); }}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400 mb-3">Tap on the map to drop a pin</p>
          )}
          <button
            onClick={handleConfirm}
            disabled={!address}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold text-base transition-all active:scale-[0.98] disabled:opacity-40"
            style={{ backgroundColor: "#E8B830" }}
            data-testid="confirm-location-btn"
          >
            <Check className="w-5 h-5" />
            Send this location
          </button>
        </div>
      </div>
    </div>
  );
}
